import React, { useEffect, useState } from "react";
import { Alert, Button, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { listFrom } from "./appraisalUtils";

const pending = (run) => ["Pending", "Running"].includes(run.status);
const resultFields = {
  records_created: "Records created",
  records_scored: "Records scored",
  records_recomputed: "Records recomputed",
  records_changed: "Scores changed",
  records_reopened: "Records reopened",
  restart_required: "Require workflow restart",
};
const componentLabels = {
  INSTRUCTIONAL_SYLLABUS: "Instructional & Syllabus Coverage",
  DISCIPLINE: "Discipline",
  CREATIVITY: "Creativity & Co-curricular",
};
const blockedKindLabels = {
  open_case: "Open case",
  undelivered_material: "Undelivered material",
  unrecorded_participation: "Unrecorded participation",
};
const blockedKindColors = {
  open_case: "red",
  undelivered_material: "orange",
  unrecorded_participation: "gold",
};
const pathwayLabels = {
  teaching: "Teaching",
  non_teaching: "Non-Teaching",
};

const blockedList = (result) =>
  Array.isArray(result?.blocked) ? result.blocked : [];
const informationalList = (result) =>
  Array.isArray(result?.informational) ? result.informational : [];
const overridesAppliedList = (result) => {
  if (Array.isArray(result?.readiness_overrides))
    return result.readiness_overrides;
  if (Array.isArray(result?.readinessOverrides))
    return result.readinessOverrides;
  return [];
};
const weightWarningList = (result) => {
  if (Array.isArray(result?.weight_warnings)) return result.weight_warnings;
  if (Array.isArray(result?.weightWarnings)) return result.weightWarnings;
  return result?.weight_warnings || result?.weightWarnings
    ? [result.weight_warnings ?? result.weightWarnings]
    : [];
};
const weightWarningText = (item) => {
  if (typeof item === "string") return item;
  if (item && typeof item === "object")
    return item.message || item.detail || item.warning || JSON.stringify(item);
  return String(item);
};

const formulaErrorList = (result) => {
  if (Array.isArray(result?.formula_errors)) return result.formula_errors;
  return result?.formula_errors ? [result.formula_errors] : [];
};

const formulaErrorText = (item) => {
  if (typeof item === "string") return item;
  if (item && typeof item === "object") {
    const staff = item.staff || item.staffName;
    const component = componentLabels[item.component] || item.component;
    const detail = item.error || item.message || item.detail;
    const context = [staff, component].filter(Boolean).join(" · ");
    if (detail) return context ? `${context}: ${detail}` : String(detail);
    return context || JSON.stringify(item);
  }
  return String(item);
};

export default function AppraisalRuns({
  cycleId,
  request,
  refreshKey,
  onCompleted,
  onBusyChange,
}) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    let timer;
    let previous = new Map();
    setRuns([]);
    setError("");
    onBusyChange?.(false);
    if (!cycleId) return undefined;
    const read = async (initial = false) => {
      if (initial) setLoading(true);
      try {
        const data = listFrom(
          await request("GET", "runs", undefined, { cycle: cycleId }),
        );
        if (cancelled) return;
        const finished = data.some(
          (run) =>
            previous.has(run.id) &&
            pending(previous.get(run.id)) &&
            !pending(run),
        );
        previous = new Map(data.map((run) => [run.id, run]));
        setRuns(data);
        onBusyChange?.(data.some(pending));
        setError("");
        // Also refresh records when returning to this tab after a job completed
        // while its activity panel was unmounted.
        if (finished || (initial && data.some((run) => !pending(run))))
          onCompleted();
        // A timeout after each response prevents overlapping requests.
        if (data.some(pending)) timer = setTimeout(() => read(), 4000);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    read(true);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cycleId, request, refreshKey, retry, onCompleted, onBusyChange]);

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Space wrap>
        <Typography.Title level={5} style={{ margin: 0 }}>
          Cycle activity
        </Typography.Title>
        <Button
          onClick={() => setRetry((value) => value + 1)}
          disabled={!cycleId}
          loading={loading}
        >
          Refresh activity
        </Button>
      </Space>
      {error && (
        <Alert
          type="error"
          showIcon
          message="Could not refresh cycle activity"
          description={error}
          action={
            <Button onClick={() => setRetry((value) => value + 1)}>
              Retry
            </Button>
          }
        />
      )}
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={runs}
        scroll={{ x: 800 }}
        pagination={{ pageSize: 5 }}
        locale={{
          emptyText: cycleId
            ? "No cycle activity yet"
            : "Choose a cycle to view activity",
        }}
        columns={[
          { title: "Operation", dataIndex: "runType" },
          { title: "Term", dataIndex: "termName" },
          {
            title: "Status",
            dataIndex: "status",
            render: (status) => (
              <Tag
                color={
                  status === "Failed"
                    ? "error"
                    : status === "Blocked"
                      ? "warning"
                      : status === "Completed"
                        ? "success"
                        : "processing"
                }
              >
                {status}
              </Tag>
            ),
          },
          {
            title: "Started",
            dataIndex: "createdAt",
            render: (date) =>
              date ? dayjs(date).format("DD MMM YYYY HH:mm") : "—",
          },
          {
            title: "Result",
            render: (_, run) => {
              const formulaErrors = formulaErrorList(run.result);
              const blocked = blockedList(run.result);
              const informational = informationalList(run.result);
              const blockedCount =
                run.result?.blocked_count ?? run.result?.blockedCount ?? blocked.length;
              const appliedOverrides = overridesAppliedList(run.result);
              const weightWarnings = weightWarningList(run.result);
              const percentagesUsed =
                run.result?.percentages_used ??
                run.result?.percentagesUsed ??
                null;
              const percentageEntries = percentagesUsed
                ? Object.entries(percentagesUsed)
                : [];
              return (
                <Space direction="vertical" size={0}>
                  {run.result?.message && <span>{run.result.message}</span>}
                  {formulaErrors.length > 0 && (
                    <div>
                      <Typography.Text type="danger" strong>
                        Formula errors ({formulaErrors.length})
                      </Typography.Text>
                      <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                        {formulaErrors.map((item, index) => (
                          <li key={`${run.id}-formula-error-${index}`}>
                            {formulaErrorText(item)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {blocked.length > 0 && (
                    <div>
                      <Typography.Text type="danger" strong>
                        Blocked items ({blockedCount})
                      </Typography.Text>
                      <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                        {blocked.map((item, index) => (
                          <li key={`${run.id}-blocked-${index}`}>
                            <Tag
                              color={blockedKindColors[item?.kind] || "default"}
                            >
                              {blockedKindLabels[item?.kind] || item?.kind || "Blocked"}
                            </Tag>{" "}
                            {item?.staff_name || item?.staffName
                              ? `${item.staff_name ?? item.staffName}`
                              : item?.staff_id ?? item?.staffId
                                ? `Staff #${item.staff_id ?? item.staffId}`
                                : null}
                            {(item?.item_id ?? item?.itemId) !== undefined &&
                              ` · Item #${item.item_id ?? item.itemId}`}{" "}
                            {item?.detail ? `— ${item.detail}` : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {informational.length > 0 && (
                    <div>
                      <Typography.Text type="secondary" strong>
                        {`Informational items (${informational.length}) — do not block the run`}
                      </Typography.Text>
                      <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                        {informational.map((item, index) => {
                          const itemId = item?.item_id ?? item?.itemId;
                          return (
                            <li key={`${run.id}-info-${index}`}>
                              <Tag color="default">
                                {blockedKindLabels[item?.kind] || item?.kind || "Info"}
                              </Tag>{" "}
                              {item?.staff_name || item?.staffName
                                ? `${item.staff_name ?? item.staffName}`
                                : item?.staff_id ?? item?.staffId
                                  ? `Staff #${item.staff_id ?? item.staffId}`
                                  : null}
                              {itemId !== undefined && ` · Item #${itemId}`}{" "}
                              {item?.detail ? `— ${item.detail}` : null}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {appliedOverrides.length > 0 && (
                    <div>
                      <Typography.Text strong>
                        Readiness overrides applied ({appliedOverrides.length})
                      </Typography.Text>
                      <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                        {appliedOverrides.map((item, index) => {
                          const itemId = item?.item_id ?? item?.itemId;
                          const by =
                            item?.overridden_by ?? item?.overriddenBy ?? "";
                          const at =
                            item?.overridden_at ?? item?.overriddenAt ?? "";
                          return (
                            <li key={`${run.id}-override-${index}`}>
                              <Tag color="blue">
                                {blockedKindLabels[item?.kind] ||
                                  item?.kind ||
                                  "Override"}
                              </Tag>{" "}
                              {itemId !== undefined
                                ? `Item #${itemId}`
                                : "Item"}{" "}
                              {item?.reason ? `— ${item.reason}` : null}{" "}
                              {(by || at) && (
                                <Typography.Text type="secondary">
                                  {by ? `by ${by}` : ""}
                                  {by && at ? " " : ""}
                                  {at
                                    ? `at ${dayjs(at).isValid() ? dayjs(at).format("DD MMM YYYY HH:mm") : at}`
                                    : ""}
                                </Typography.Text>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {weightWarnings.length > 0 && (
                    <div>
                      <Typography.Text type="warning" strong>
                        Weight warnings ({weightWarnings.length})
                      </Typography.Text>
                      <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                        {weightWarnings.map((item, index) => (
                          <li key={`${run.id}-weight-warning-${index}`}>
                            {weightWarningText(item)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {percentageEntries.length > 0 && (
                    <div>
                      <Typography.Text strong>Percentages used</Typography.Text>
                      <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                        {percentageEntries.map(([pathway, info]) => {
                          const shares = info?.shares;
                          const sharesText = Array.isArray(shares)
                            ? shares.join(", ")
                            : shares && typeof shares === "object"
                              ? Object.entries(shares)
                                  .map(([key, value]) => `${key}: ${value}`)
                                  .join(", ")
                              : shares ?? "—";
                          const fiveR =
                            info?.five_r_weight ?? info?.fiveRWeight;
                          return (
                            <li key={`${run.id}-percentages-${pathway}`}>
                              <Tag color="geekblue">
                                {pathwayLabels[pathway] || pathway}
                              </Tag>{" "}
                              {info?.mode && (
                                <Tag>{info.mode}</Tag>
                              )}{" "}
                              <span>
                                Shares: {String(sharesText)}
                                {fiveR !== undefined && fiveR !== null
                                  ? ` · 5R weight: ${fiveR}`
                                  : ""}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {Object.entries(resultFields)
                    .filter(([key]) => run.result?.[key] !== undefined)
                    .map(([key, label]) => (
                      <span key={key}>
                        {label}:{" "}
                        {Array.isArray(run.result[key])
                          ? run.result[key].length
                          : String(run.result[key])}
                      </span>
                    ))}
                  {run.errorLog && (
                    <Typography.Text type="danger">
                      {run.errorLog}
                    </Typography.Text>
                  )}
                  {run.remarks && (
                    <Typography.Text type="secondary">
                      {run.remarks}
                    </Typography.Text>
                  )}
                </Space>
              );
            },
          },
        ]}
      />
    </Space>
  );
}
